import { z } from 'zod'

import { lenientList } from '@/schemas/common'

/**
 * Mitra's brief — the day's market story behind Home's Brief card and its reader.
 *
 * PROPOSED, not live: mm-be has no brief endpoint yet. The contract is in
 * `mm-homex-2026-10-28/asks/brief.md`; `briefService` sends nothing until it ships.
 *
 * Prose fields may wrap a figure or a ticker in `**…**` for emphasis (the reader sets it in
 * 600 primary ink, as the mock does). Nothing else in them is markup: a glossary word is a
 * plain word, matched against `terms`.
 */

/** The four reader sections, in the order they are drawn. */
export const BRIEF_SECTION_IDS = ['sectors', 'breadth', 'money', 'coming-up'] as const

export const briefSectionIdSchema = z.enum(BRIEF_SECTION_IDS)

export const briefSectionSchema = z.object({
  id: briefSectionIdSchema,
  heading: z.string().min(1),
  body: z.string().min(1),
})

/** A word the reader explains on hover: "dividend" and its one-line meaning. */
export const briefTermSchema = z.object({
  text: z.string().min(1),
  plain: z.string().min(1),
})

export const briefNarrativeSchema = z.object({
  /** ISO instant the story was written against. */
  asOf: z.string().min(1),
  /** `open`: written mid-session ("so far today"). `closed`: after the close. */
  session: z.enum(['open', 'closed']),
  headline: z.string().min(1),
  /** The card's two lines under the headline. */
  summary: z.string().min(1),
  lede: z.string().min(1),
  readMinutes: z.number().int().min(1),
  inShort: z.array(z.string().min(1)).max(5),
  /* One unreadable section costs that section, not the brief. */
  sections: lenientList(briefSectionSchema, 'brief section'),
  terms: lenientList(briefTermSchema, 'brief term'),
  /** The member's own answer to "Was this explanation helpful?"; null when not given or signed out. */
  myVote: z.boolean().nullable(),
})

export const briefVoteSchema = z.object({ helpful: z.boolean() })

export type BriefSectionId = z.infer<typeof briefSectionIdSchema>
export type BriefSection = z.infer<typeof briefSectionSchema>
export type BriefTerm = z.infer<typeof briefTermSchema>
export type BriefNarrative = z.infer<typeof briefNarrativeSchema>
export type BriefVote = z.infer<typeof briefVoteSchema>
